/* eslint-disable @typescript-eslint/naming-convention */
/**
 * The module defines types, components, context and utility methods related to
 * accessing the configuration data for use by the client side rendered application.
 */
import React, { createContext, useContext } from 'react';

/**
 * Type for the configuration data
 */
interface DatadogConfig {
    'worker-adhoc-shifts.datadog.applicationId': string;
    'worker-adhoc-shifts.datadog.clientToken': string;
}

interface BatchCountConfig {
    'worker-adhoc-shifts.batch.count': string;
    'worker-adhoc-shifts.batch.aggtime.count': string;
}

interface AdHocShiftsConfig {
    'worker-email': string;
    'worker-id': string;
    'country-code': string;
    'escape-uri': string;
}

export type FlexInstance =
    | { currency: 'GBP'; currencySymbol: '£'; countryCode: 'GB' }
    | { currency: 'USD'; currencySymbol: '$'; countryCode: 'US' };

export interface FlexApiConfig {
    instance: FlexInstance;
    url: string;
}

interface ClientConfig extends DatadogConfig, BatchCountConfig, AdHocShiftsConfig {
    level: string;
    publicPath: string;
    release: string;
    appUrl: string;
}

export type ClientConfigType = Partial<ClientConfig>;

/**
 * Context object storing the configuration data
 */
export const ClientConfigContext = createContext<ClientConfigType | undefined>(undefined);

/**
 * Utility hook to provide the client configuration context value.  If no
 * configuration is available before the hook is dynamically invoked, then an error
 * is raised
 */
export const useClientConfig = (): ClientConfigType => {
    const context = useContext(ClientConfigContext);
    if (!context) {
        throw new Error('Client configuration context must be defined before being used');
    }

    return context;
};

interface ClientConfigProviderProps {
    clientConfig: ClientConfigType;
}

/**
 * A Provider higher order component that makes the client configuration context
 * available to children component.
 * @param clientConfig
 * @param children
 * @constructor
 */
export const ClientConfigProvider = ({
    clientConfig,
    children,
}: React.PropsWithChildren<ClientConfigProviderProps>): React.JSX.Element => {
    return <ClientConfigContext.Provider value={clientConfig}>{children}</ClientConfigContext.Provider>;
};
